// DataTab.js — Справочники (Дизайн как у Контрагентов + Модалка редактирования транзакций)

import React, { useState, useMemo, useEffect, useRef } from "react";

import { FolderOpen, Users, Tags, Plus, Search, Filter, Archive, Check, X, ArrowRight, ArrowLeft, History, FileText, RotateCcw, Trash2, Edit3, ChevronDown, ChevronUp, Wallet, CreditCard, Target, Eye, EyeOff, Minus } from "lucide-react";
import { useTheme, uid, fmt, Card, Btn, PageHeader, TabSwitch, Confirm, NumInput, HelpTooltip, addAuditLog, computeFundWriteoff, DEFAULT_PERMS, getLocale, PeriodFilter, getPeriodRange, isInPeriod } from "./shared";
import ContractorsTab from "./ContractorsTab";
import ArchiveTab from "./ArchiveTab";
import { QuickIncomeModal, QuickExpenseModal } from "./AFTab";
import { HELP_TEXTS } from "./HelpText";
import { S } from './strings';



// А также добавь импорт HistoryModal (сразу после импорта Confirm):
import { HistoryModal } from "./HistoryModal";


export default function DataTab({ data, uOrg, user, perms, isCompact, apiSync }) {
  // НОВОЕ: По умолчанию открываем новую вкладку 'accounts'
  const [activeTab, setActiveTab] = useState('accounts');
  const t = useTheme(); // Получаем тему для передачи в компоненты

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <TabSwitch
          tabs={[
            // НОВОЕ: Добавлена вкладка Счета и фонды на первое место
            { id: 'accounts', label: S().data.accountsTab, icon: Wallet },
            { id: 'contractors', label: S().data.contractorsTab, icon: Users },
            { id: 'categories', label: S().data.categoriesTab, icon: Tags },
            { id: 'archive', label: S().data.archiveTab, icon: Archive }
          ]}
          active={activeTab} onChange={setActiveTab}
        />
      </div>

      {/* НОВОЕ: Вызов компонента новой вкладки */}
      {activeTab === 'accounts' && <AccountsFundsTab data={data} uOrg={uOrg} user={user} perms={perms} isCompact={isCompact} t={t} apiSync={apiSync} />}
      {activeTab === 'contractors' && <ContractorsTab data={data} uOrg={uOrg} user={user} perms={perms} isCompact={isCompact} apiSync={apiSync} />}
      {activeTab === 'categories' && <CategoriesTab data={data} uOrg={uOrg} user={user} perms={perms} isCompact={isCompact} t={t} apiSync={apiSync} />}
      {activeTab === 'archive' && <ArchiveTab data={data} uOrg={uOrg} user={user} perms={perms} apiSync={apiSync} />}
    </div>
  );
}

function CategoriesTab({ data, uOrg, user, perms, isCompact, t, apiSync }) {
  const canCat = perms.all || perms.manageCategories;
  const [q, setQ] = useState('');
  // Раздельные сортировки для двух колонок (Доходы / Расходы)
  const [sortIncomes, setSortIncomes] = useState('sum_desc');
  const [sortExpenses, setSortExpenses] = useState('sum_desc');
  const [editModal, setEditModal] = useState(null);
  const [viewModal, setViewModal] = useState(null);
  const [arcId, setArcId] = useState(null);
  // Период для подсчёта оборотов по категориям
  const [period, setPeriod] = useState('30d');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingTx, setEditingTx] = useState(null);
  // Свернутый поиск (раскрывается по клику на иконку)
  const [searchOpen, setSearchOpen] = useState(false);
  const [cSearch, setCSearch] = useState(''); // Для поиска контрагента
  const [showCDropdown, setShowCDropdown] = useState(false); // Для открытия списка

  // showHelp state removed — now using HelpTooltip component

  // НОВЫЙ БЛОК: Отслеживание клика вне списка
  const dropdownRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const transactions = data.transactions || [];
  const incCats = (data.incomeCategories || []).filter(c => !c.archived).map(c => ({ ...c, isIncome: true }));
  const expCats = (data.expenseCategories || []).filter(c => !c.archived).map(c => ({ ...c, isIncome: false }));
  let allCats = [...incCats, ...expCats];

  // Транзакции по id категории, отфильтрованные по выбранному периоду
  const txByCategoryId = useMemo(() => {
    const range = getPeriodRange(period, dateFrom, dateTo);
    const map = {};
    transactions.forEach(tx => {
      if (!isInPeriod(tx.date, range)) return;
      if (tx.incomeCategoryId) {
        if (!map[tx.incomeCategoryId]) map[tx.incomeCategoryId] = [];
        map[tx.incomeCategoryId].push(tx);
      }
      if (tx.expenseCategoryId) {
        if (!map[tx.expenseCategoryId]) map[tx.expenseCategoryId] = [];
        map[tx.expenseCategoryId].push(tx);
      }
    });
    return map;
  }, [transactions, period, dateFrom, dateTo]);

  const withMetrics = useMemo(() => {
    let res = allCats;
    if (q.trim()) res = res.filter(c => (c.name || '').toLowerCase().includes(q.toLowerCase()));
    return res.map(c => {
      const myTx = txByCategoryId[c.id] || [];
      const vol = myTx.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
      const lastOp = myTx.length > 0 ? Math.max(...myTx.map(tx => new Date(tx.date).getTime())) : 0;
      return { ...c, _vol: vol, _lastOp: lastOp };
    });
  }, [allCats, txByCategoryId, q]);

  const sortFn = (sortBy) => (a, b) => {
    if (sortBy === 'name_asc') return a.name.localeCompare(b.name);
    if (sortBy === 'name_desc') return b.name.localeCompare(a.name);
    if (sortBy === 'sum_desc') return b._vol - a._vol;
    if (sortBy === 'sum_asc') return a._vol - b._vol;
    if (sortBy === 'recent') return b._lastOp - a._lastOp;
    return 0;
  };

  const incomes = useMemo(
    () => withMetrics.filter(c => c.isIncome).slice().sort(sortFn(sortIncomes)),
    [withMetrics, sortIncomes]
  );
  const expenses = useMemo(
    () => withMetrics.filter(c => !c.isIncome).slice().sort(sortFn(sortExpenses)),
    [withMetrics, sortExpenses]
  );

  // Маленький селектор сортировки для колонки
  const SortSelect = ({ value, onChange }) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ padding:'5px 8px', borderRadius:7, background:t.bgInput, color:t.text, border:`1px solid ${t.border}`, fontSize:11, cursor:'pointer' }}>
      <option value="sum_desc">{S().data.sortVolDesc}</option>
      <option value="sum_asc">{S().data.sortVolAsc}</option>
      <option value="name_asc">{S().data.sortNameAsc}</option>
      <option value="name_desc">{S().data.sortNameDesc}</option>
      <option value="recent">{S().data.sortRecent}</option>
    </select>
  );

  const doArchive = async () => {
    const isInc = arcId.startsWith('inc_');
    const key = isInc ? 'incomeCategories' : 'expenseCategories';
    const oldCat = data[key].find(c => c.id === arcId);
    if (!apiSync || !apiSync.syncArchiveCategory) { setArcId(null); setViewModal(null); return; }
    const r = await apiSync.syncArchiveCategory(arcId);
    if (!r) return; // handleSyncError показал alert
    addAuditLog(uOrg, user, { type: 'CAT_ARCHIVE', desc: `Категория отправлена в архив: ${oldCat?.name}`, before: oldCat, after: { ...oldCat, archived: true } });
    setArcId(null); setViewModal(null);
  };

  const renderRow = (c, typeColor) => {
    let subText = '';
    if (c.isIncome) subText = c.accountId ? S().data.linkedToAccount(data.accounts.find(a => a.id === c.accountId)?.name || S().data.deletedLink) : S().data.notLinkedToAccount;
    else subText = c.fundId ? S().data.linkedToFund(data.funds.find(f => f.id === c.fundId)?.name || S().data.deletedLink) : S().data.notLinkedToFund;

    // Для базовых "Без категории" покажем особый текст
    if (c.id === 'uncat_inc' || c.id === 'uncat_exp') {
      subText = S().data.baseSystemCategory;
    }

    return (
      <div key={c.id} onClick={() => { setViewModal(c); }} style={{ padding: '12px 16px', background: t.bgInput, borderRadius: 10, border: `1px solid ${c.id.startsWith('uncat_') ? typeColor : t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = t.bgCard} onMouseLeave={e => e.currentTarget.style.background = t.bgInput}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${typeColor}15`, color: typeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Tags size={16} /></div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: t.text }}>{c.name}</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>{subText}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0, marginLeft: 12 }}>
          {c._vol > 0 ? <span style={{ fontSize: 13, fontWeight: 800, color: typeColor }}>{fmt(c._vol)}</span> : <span style={{ fontSize: 12, color: t.textMuted, fontWeight: 600 }}>{S().data.noTurnover}</span>}
        </div>
      </div>
    );
  };



  return (
    <div>
      {arcId && (() => {
        const cat = [...(data.incomeCategories || []), ...(data.expenseCategories || [])].find(c => c.id === arcId);
        const cName = cat?.name || '';
        return <Confirm
          msg={S().data.archiveCategoryConfirm(cName)}
          extra={<div style={{ fontSize: 13, color: t.textMuted, marginBottom: 20, lineHeight: 1.5 }}>{S().data.archiveCategoryExtra}</div>}
          onYes={doArchive}
          onNo={() => setArcId(null)}
        />;
      })()}

      {/* НОВОЕ: Подключаем компонент CategoryModal вместо старой функции */}
      {viewModal && <CategoryModal cat={viewModal} data={data} uOrg={uOrg} onClose={() => setViewModal(null)} t={t} isCompact={isCompact} apiSync={apiSync} />}

      {/* === МОДАЛКА РЕДАКТИРОВАНИЯ ТРАНЗАКЦИИ === */}
      {editingTx && <TxEditModal tx={editingTx} data={data} uOrg={uOrg} onClose={() => setEditingTx(null)} t={t} apiSync={apiSync} />}

      {/* === МОДАЛКА ДОБАВЛЕНИЯ / РЕДАКТИРОВАНИЯ КАТЕГОРИИ === */}
      {editModal && (
        // НОВОЕ: Очищаем showHelp при закрытии по клику на фон
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (() => { setEditModal(null); setCSearch(''); setShowCDropdown(false); })(); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: 24, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>

            {/* ШАПКА — единый стиль: заголовок слева, иконки справа */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{editModal.id ? S().data.editCategory : S().data.newCategory}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <HelpTooltip text={HELP_TEXTS.category} size={15} />
                <button onClick={() => { setEditModal(null); setCSearch(''); setShowCDropdown(false); }} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}>
                  <X size={18} />
                </button>
              </div>
            </div>


            {!editModal.id && (
              <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="radio" checked={editModal.isIncome} onChange={() => setEditModal({ ...editModal, isIncome: true })} name="ctype" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.accentGreen }}>{S().data.incomeType}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="radio" checked={!editModal.isIncome} onChange={() => setEditModal({ ...editModal, isIncome: false })} name="ctype" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.accentRed }}>{S().data.expenseType}</span>
                </label>
              </div>
            )}

            <input value={editModal.name} onChange={e => setEditModal({ ...editModal, name: e.target.value })} placeholder={S().data.categoryNamePlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, marginBottom: 16 }} />

            {editModal.isIncome ? (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().data.linkToAccountLabel}</label>
                <select value={editModal.accountId || ''} onChange={e => setEditModal({ ...editModal, accountId: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text }}>
                  <option value="">{S().data.noAccountLink}</option>
                  {data.accounts.filter(a => !a.archived).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().data.linkToFundLabel}</label>
                <select value={editModal.fundId || ''} onChange={e => setEditModal({ ...editModal, fundId: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text }}>
                  <option value="">{S().data.noFundLink}</option>
                  {data.funds.filter(f => !f.archived).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
            )}

            {/* УМНАЯ ПРИВЯЗКА КОНТРАГЕНТОВ (ВЫПАДАЮЩИЙ СПИСОК) */}
            <div style={{ marginBottom: 24, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{S().data.linkedContractors}</div>

              {/* Блок ВЫБРАННЫХ контрагентов (с крестиками) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {(editModal.contractorIds || []).map(cId => {
                  const con = (data.contractors || []).find(x => x.id === cId);
                  if (!con) return null;
                  return (
                    <div key={cId} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', fontSize: 12, borderRadius: 14, background: t.accent, color: '#fff', fontWeight: 600 }}>
                      {con.name}
                      <X size={14} style={{ cursor: 'pointer', opacity: 0.8 }} onClick={() => setEditModal(p => ({ ...p, contractorIds: p.contractorIds.filter(x => x !== cId) }))} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.8} />
                    </div>
                  );
                })}
                {(editModal.contractorIds || []).length === 0 && <span style={{ fontSize: 12, color: t.textMuted }}>{S().data.noneSelected}</span>}
              </div>

              {/* Строка поиска и выпадающий список */}
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', background: t.bgCard, borderRadius: 8, border: `1px solid ${t.border}`, padding: '0 10px' }}>
                  <Search size={14} color={t.textMuted} />
                  <input
                    value={cSearch}
                    onChange={e => { setCSearch(e.target.value); setShowCDropdown(true); }}
                    onFocus={() => setShowCDropdown(true)}
                    placeholder={S().data.searchByNameOrBin}
                    style={{ flex: 1, padding: '10px', border: 'none', background: 'transparent', fontSize: 13, color: t.text, outline: 'none' }}
                  />
                  {showCDropdown ? (
                    <ChevronUp size={16} color={t.textMuted} style={{ cursor: 'pointer' }} onClick={() => setShowCDropdown(false)} />
                  ) : (
                    <ChevronDown size={16} color={t.textMuted} style={{ cursor: 'pointer' }} onClick={() => setShowCDropdown(true)} />
                  )}
                </div>

                {/* Сам выпадающий список (Dropdown) */}
                {showCDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, maxHeight: 200, overflowY: 'auto', zIndex: 50, boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                    {(() => {
                      const available = (data.contractors || []).filter(c => !c.archived && (editModal.isIncome ? c.isCustomer !== false : c.isSupplier !== false));
                      const filtered = available.filter(c => (c.name || '').toLowerCase().includes(cSearch.toLowerCase()) || (c.bin || '').includes(cSearch));
                      if (filtered.length === 0) return <div style={{ padding: 12, fontSize: 13, color: t.textMuted, textAlign: 'center' }}>{S().data.contractorsNotFound}</div>;

                      return filtered.map(c => {
                        const isSelected = editModal.contractorIds?.includes(c.id);
                        return (
                          <div
                            key={c.id}
                            onClick={() => { setEditModal(p => ({ ...p, contractorIds: isSelected ? p.contractorIds.filter(x => x !== c.id) : [...(p.contractorIds || []), c.id] })); }}
                            style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${t.border}`, background: isSelected ? `${t.accent}15` : 'transparent', transition: '0.2s' }}
                            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = t.bgInput }}
                            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                          >
                            <div>
                              <div style={{ fontWeight: isSelected ? 800 : 600, color: t.text }}>{c.name}</div>
                              {c.bin && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{S().data.binIinLabel}{c.bin}</div>}
                            </div>
                            {isSelected ? <Check size={16} color={t.accent} /> : <Plus size={16} color={t.textMuted} style={{ opacity: 0.5 }} />}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>

            <Btn variant="success" style={{ width: '100%', justifyContent: 'center', padding: 14 }} onClick={async () => {
              if (!editModal.name.trim()) return;
              if (!apiSync) { alert((S().common && S().common.unknownError) || 'Нет связи с сервером'); return; }
              const _dupKey = editModal.isIncome ? 'incomeCategories' : 'expenseCategories';
              const _dupNm = editModal.name.trim().toLowerCase();
              if ((data[_dupKey] || []).some(x => !x.archived && x.id !== editModal.id && (x.name || '').trim().toLowerCase() === _dupNm)) {
                alert(`Категория «${editModal.name.trim()}» уже есть в этом разделе (${editModal.isIncome ? 'доходы' : 'расходы'})`); return;
              }
              const isNew = !editModal.id;
              let nc;
              if (editModal.isIncome) {
                nc = isNew ? { id: `inc_${uid()}`, archived: false } : { ...editModal };
                nc.name = editModal.name.trim();
                nc.accountId = editModal.accountId || '';
                nc.contractorIds = editModal.contractorIds || [];
              } else {
                nc = isNew ? { id: `cat_${uid()}`, archived: false } : { ...editModal };
                nc.name = editModal.name.trim();
                nc.fundId = editModal.fundId || '';
                nc.contractorIds = editModal.contractorIds || [];
              }
              // Sequential (2026-06-11): ждём backend → refetch-categories обновит список из БД.
              const payload = { ...nc, type: editModal.isIncome ? 'income' : 'expense' };
              let r = null;
              try {
                if (isNew) r = await apiSync.syncCreateCategory(payload);
                else r = await apiSync.syncUpdateCategory(nc.id, payload);
              } catch (e) { r = null; }
              if (!r) return;
              addAuditLog(uOrg, user, {
                type: isNew ? 'CAT_CREATE' : 'CAT_UPDATE',
                desc: `${isNew ? 'Создана' : 'Обновлена'} категория: ${nc.name}`,
                after: { name: nc.name, isIncome: editModal.isIncome }
              });
              setEditModal(null);
              setCSearch('');
              setShowCDropdown(false);
            }}><Check size={18} /> {S().common.save}</Btn>
          </div>
        </div>
      )}

      <PageHeader
        title={S().data.categoriesTitle}
        sub={S().data.categoriesSubtitle(allCats.length)}
        right={canCat && (
          <Btn onClick={() => setEditModal({ name: '', isIncome: true, fundId: '', accountId: '', contractorIds: [] })}><Plus size={16} /> {S().data.addCategory}</Btn>
        )}
      />

      <Card style={{ marginBottom: 20, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          <PeriodFilter
            label={S().periodFilter?.categoriesLabel || 'Суммы по категориям за:'}
            period={period} setPeriod={setPeriod}
            dateFrom={dateFrom} setDateFrom={setDateFrom}
            dateTo={dateTo} setDateTo={setDateTo}
            isCompact={isCompact}
          />
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {searchOpen ? (
              <div style={{ display: 'flex', alignItems: 'center', background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}`, padding: '0 10px', width: 240 }}>
                <Search size={15} color={t.textMuted} />
                <input
                  autoFocus
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder={S().data.searchCategory}
                  style={{ border: 'none', background: 'transparent', padding: '8px 8px', color: t.text, flex: 1, outline: 'none' }}
                />
                <button
                  onClick={() => { setQ(''); setSearchOpen(false); }}
                  style={{ background: 'transparent', border: 'none', color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  title="Закрыть поиск"
                ><X size={15} /></button>
              </div>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                style={{ width: 36, height: 36, borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title={S().data.searchCategory}
              ><Search size={16} /></button>
            )}
          </div>
        </div>
      </Card>

      {withMetrics.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: t.textMuted }}><Tags size={40} style={{ margin: '0 auto 16px', opacity: 0.5 }} /><div style={{ fontSize: 16, fontWeight: 700 }}>{S().data.categoriesNotFound}</div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 24 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: t.accentGreen }}><ArrowRight size={20} /> {S().data.categoriesForAccounts}</div>
              <SortSelect value={sortIncomes} onChange={setSortIncomes}/>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{incomes.map(c => renderRow(c, t.accentGreen))}</div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: t.accentRed }}><ArrowLeft size={20} /> {S().data.categoriesForFunds}</div>
              <SortSelect value={sortExpenses} onChange={setSortExpenses}/>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{expenses.map(c => renderRow(c, t.accentRed))}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// === МОДАЛЬНОЕ ОКНО КАТЕГОРИИ ===
function CategoryModal({ cat, data, uOrg, onClose, t, isCompact, apiSync }) {
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState(cat.name);
  const [linkedId, setLinkedId] = useState(cat.isIncome ? (cat.accountId || '') : (cat.fundId || ''));
  const [contractorIds, setContractorIds] = useState(cat.contractorIds || []);
  // Fix (2026-06-09): «показываемые» имя/привязка — обновляются при сохранении (cat prop —
  // снимок, не обновляется на refetch), чтобы в открытой модалке имя менялось сразу.
  const [displayName, setDisplayName] = useState(cat.name);
  const [displayLinkedId, setDisplayLinkedId] = useState(cat.isIncome ? (cat.accountId || '') : (cat.fundId || ''));

  const [period, setPeriod] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingTx, setEditingTx] = useState(null);
  // showHelp state removed — now using HelpTooltip component

  const [cSearch, setCSearch] = useState('');
  const [showCDropdown, setShowCDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowCDropdown(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const save = async () => {
    if (!name.trim()) return alert(S().data.enterName);
    const _dupKey = cat.isIncome ? 'incomeCategories' : 'expenseCategories';
    const _dupNm = name.trim().toLowerCase();
    if ((data[_dupKey] || []).some(x => !x.archived && x.id !== cat.id && (x.name || '').trim().toLowerCase() === _dupNm)) {
      return alert(`Категория «${name.trim()}» уже есть в этом разделе`);
    }
    if (!apiSync || !apiSync.syncUpdateCategory) { setIsEdit(false); return; }
    // Sequential (2026-06-11): ждём backend → refetch-categories обновит список из БД.
    const payload = cat.isIncome
      ? { name: name.trim(), accountId: linkedId, contractorIds }
      : { name: name.trim(), fundId: linkedId, contractorIds };
    const r = await apiSync.syncUpdateCategory(cat.id, payload);
    if (!r) return; // handleSyncError показал alert
    setDisplayName(name.trim());
    setDisplayLinkedId(linkedId);
    setIsEdit(false);
  };

  const doArchive = async () => {
    const key = cat.isIncome ? 'incomeCategories' : 'expenseCategories';
    const activeCount = (data[key] || []).filter(c => !c.archived).length;
    if (activeCount <= 1) {
      alert(S().data.archiveCategoryMinOne);
      return;
    }
    if (!window.confirm(S().data.archiveCategoryQuestion)) return;
    if (!apiSync || !apiSync.syncArchiveCategory) { onClose(); return; }
    const r = await apiSync.syncArchiveCategory(cat.id);
    if (!r) return; // handleSyncError показал alert
    onClose();
  };

  const linkName = cat.isIncome
    ? (data.accounts.find(a => a.id === (isEdit ? linkedId : displayLinkedId))?.name || S().data.noLinkGeneral)
    : (data.funds.find(f => f.id === (isEdit ? linkedId : displayLinkedId))?.name || S().data.noLinkGeneral);

  const typeColor = cat.isIncome ? t.accentGreen : t.accentRed;

  const now = new Date();
  let start = null, end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  if (period === 'month') start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  else if (period === '6months') start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
  else if (period === 'year') start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  else if (period === 'custom') { start = dateFrom ? new Date(dateFrom) : null; end = dateTo ? new Date(dateTo + 'T23:59:59') : end; }

  const myTx = (data.transactions || []).filter(tx => {
    if (cat.isIncome ? tx.incomeCategoryId !== cat.id : tx.expenseCategoryId !== cat.id) return false;
    if (!start && period !== 'custom') return true;
    const txDate = new Date(tx.date).getTime();
    return !(start && txDate < start.getTime()) && !(end && txDate > end.getTime());
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalVol = myTx.reduce((s, tx) => s + tx.amount, 0);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>

      {editingTx && <TxEditModal tx={editingTx} data={data} uOrg={uOrg} onClose={() => setEditingTx(null)} t={t} apiSync={apiSync} />}

      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, width: 600, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        <div style={{ padding: isCompact ? '16px' : '20px 24px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', gap: isCompact ? 10 : 14, alignItems: 'center' }}>
            <div style={{ width: 45, height: 45, borderRadius: 12, background: `${typeColor}15`, color: typeColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Tags size={22} /></div>
            <div>
              {/* Убрали HelpCircle отсюда */}
              <div style={{ fontSize: 20, fontWeight: 800 }}>{displayName}</div>
              <div style={{ fontSize: 13, color: t.textMuted, marginTop: 2 }}>{cat.isIncome ? S().data.incomeType : S().data.expenseType} · {linkName}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Добавили кнопку Справки сюда, как у Контрагентов */}
            <HelpTooltip text={HELP_TEXTS.category} size={15} />

            <button onClick={() => setIsEdit(!isEdit)} title={S().data.editTooltip} style={{ background: t.bgInput, color: t.textSec, padding: 8, borderRadius: 8, border: `1px solid ${t.border}`, cursor: 'pointer' }}><Edit3 size={16} /></button>
            <button onClick={doArchive} title={S().data.toArchiveTooltip} style={{ background: `${t.accentOrange}15`, color: t.accentOrange, padding: 8, borderRadius: 8, border: 'none', cursor: 'pointer' }}><Archive size={16} /></button>

            <button onClick={onClose} title={S().data.closeTooltip} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
          </div>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto' }}>



          {isEdit && !cat.isVirtual && (
            <div style={{ marginBottom: 24, padding: 16, background: t.bgInput, borderRadius: 12, border: `1px dashed ${t.border}` }}>
              <label style={{ fontSize: 12, color: t.textMuted, marginBottom: 4, display: 'block', fontWeight: 700 }}>{S().data.categoryNameLabel}</label>
              <input value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', marginBottom: 16, padding: '10px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text }} />

              <label style={{ fontSize: 12, color: t.textMuted, marginBottom: 4, display: 'block', fontWeight: 700 }}>
                {cat.isIncome ? S().data.linkToAccountEdit : S().data.linkToFundEdit}
              </label>
              <select value={linkedId} onChange={e => setLinkedId(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: t.bgCard, border: `1px solid ${t.border}`, color: t.text, marginBottom: 16 }}>
                <option value="">{S().data.noLinkGeneralCategory}</option>
                {cat.isIncome
                  ? data.accounts.filter(a => !a.archived).map(a => <option key={a.id} value={a.id}>{a.name}</option>)
                  : data.funds.filter(f => !f.archived).map(f => <option key={f.id} value={f.id}>{f.name}</option>)
                }
              </select>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 8 }}>{S().data.linkedContractors}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {contractorIds.map(cId => {
                    const con = (data.contractors || []).find(x => x.id === cId);
                    if (!con) return null;
                    return (
                      <div key={cId} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', fontSize: 12, borderRadius: 14, background: t.accent, color: '#fff', fontWeight: 600 }}>
                        {con.name}
                        <X size={14} style={{ cursor: 'pointer', opacity: 0.8 }} onClick={() => setContractorIds(p => p.filter(x => x !== cId))} />
                      </div>
                    );
                  })}
                  {contractorIds.length === 0 && <span style={{ fontSize: 12, color: t.textMuted }}>{S().data.noneSelected}</span>}
                </div>

                <div ref={dropdownRef} style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', background: t.bgCard, borderRadius: 8, border: `1px solid ${t.border}`, padding: '0 10px' }}>
                    <Search size={14} color={t.textMuted} />
                    <input
                      value={cSearch} onChange={e => { setCSearch(e.target.value); setShowCDropdown(true); }} onFocus={() => setShowCDropdown(true)}
                      placeholder={S().data.searchByNameOrBin} style={{ flex: 1, padding: '10px', border: 'none', background: 'transparent', fontSize: 13, color: t.text, outline: 'none' }}
                    />
                    {showCDropdown ? <ChevronUp size={16} color={t.textMuted} style={{ cursor: 'pointer' }} onClick={() => setShowCDropdown(false)} /> : <ChevronDown size={16} color={t.textMuted} style={{ cursor: 'pointer' }} onClick={() => setShowCDropdown(true)} />}
                  </div>

                  {showCDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, maxHeight: 200, overflowY: 'auto', zIndex: 50, boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                      {(() => {
                        const available = (data.contractors || []).filter(c => !c.archived && (cat.isIncome ? c.isCustomer !== false : c.isSupplier !== false));
                        const filtered = available.filter(c => (c.name || '').toLowerCase().includes(cSearch.toLowerCase()) || (c.bin || '').includes(cSearch));
                        if (filtered.length === 0) return <div style={{ padding: 12, fontSize: 13, color: t.textMuted, textAlign: 'center' }}>{S().data.notFoundShort}</div>;

                        return filtered.map(c => {
                          const isSelected = contractorIds.includes(c.id);
                          return (
                            <div key={c.id} onClick={() => setContractorIds(p => isSelected ? p.filter(x => x !== c.id) : [...p, c.id])} style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${t.border}`, background: isSelected ? `${t.accent}15` : 'transparent' }}>
                              <div style={{ fontWeight: isSelected ? 800 : 600 }}>{c.name}</div>
                              {isSelected ? <Check size={16} color={t.accent} /> : <Plus size={16} color={t.textMuted} style={{ opacity: 0.5 }} />}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <Btn variant="success" onClick={save} style={{ width: '100%', justifyContent: 'center' }}><Check size={18} /> {S().data.saveChanges}</Btn>
            </div>
          )}

          {/* НОВОЕ: Отображение привязок в режиме просмотра */}
          {!isEdit && !cat.isVirtual && (
            <div style={{ marginBottom: 24, display: 'grid', gap: 12 }}>
              {/* Привязанный счет или фонд */}
              {linkedId && (
                <div style={{ padding: 14, background: t.bgInput, borderRadius: 12, border: `1px solid ${t.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}><Tags size={15} color={t.accent} /> {S().data.linkSection}</div>
                  <span style={{ fontSize: 12, padding: '4px 12px', borderRadius: 12, background: `${typeColor}15`, color: typeColor, fontWeight: 700 }}>
                    {cat.isIncome ? S().data.accountPrefix : S().data.fundPrefix}{linkName}
                  </span>
                </div>
              )}

              {/* Привязанные контрагенты */}
              {(cat.contractorIds || []).length > 0 && (
                <div style={{ padding: 14, background: t.bgInput, borderRadius: 12, border: `1px solid ${t.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}><Users size={15} color={t.accent} /> {S().data.linkedContractors}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(cat.contractorIds || []).map(cId => {
                      const con = (data.contractors || []).find(x => x.id === cId);
                      if (!con) return null;
                      return <span key={cId} style={{ fontSize: 12, padding: '4px 12px', borderRadius: 12, background: `${typeColor}15`, color: typeColor, fontWeight: 700 }}>{con.name}</span>;
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}><FileText size={16} color={t.accent} /> {S().data.categoryOperations}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <select value={period} onChange={e => setPeriod(e.target.value)} style={{ fontSize: 12, padding: '6px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text }}>
                <option value="all">{S().data.periodAll}</option><option value="month">{S().data.periodMonth}</option><option value="6months">{S().data.period6months}</option><option value="year">{S().data.periodYear}</option><option value="custom">{S().data.periodCustom}</option>
              </select>
              {period === 'custom' && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ fontSize: 12, padding: '6px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}` }} /> <span style={{ color: t.textMuted }}>-</span> <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ fontSize: 12, padding: '6px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}` }} />
                </div>
              )}
            </div>
          </div>

          <div style={{ padding: 16, background: `${typeColor}10`, borderRadius: 12, border: `1px solid ${typeColor}30`, marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: typeColor, fontWeight: 700, marginBottom: 8 }}>{S().data.totalTurnover}</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>{fmt(totalVol)}</div>
          </div>

          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><History size={16} color={t.accent} /> {S().data.operationsList}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {myTx.length === 0 ? <div style={{ fontSize: 13, color: t.textMuted, textAlign: 'center', padding: 20 }}>{S().data.noOpsInPeriod}</div> : myTx.map(tx => (
              <div key={tx.id} onClick={() => setEditingTx(tx)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}`, cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.background = t.bgCard} onMouseLeave={e => e.currentTarget.style.background = t.bgInput}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{tx.comment || S().common.noDescription}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>{new Date(tx.date).toLocaleDateString(getLocale())}</div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: typeColor }}>
                  {cat.isIncome ? '+' : '-'}{fmt(tx.amount)}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

// Переиспользуемая функция модалки редактирования транзакции (Встроена сюда для удобства)
export function TxEditModal({ tx, data, uOrg, onClose, t, apiSync }) {
  const [catId, setCatId] = useState(tx.type === 'income' ? (tx.incomeCategoryId || '') : (tx.expenseCategoryId || ''));
  const [comment, setComment] = useState(tx.comment || '');
  const [contractorId, setContractorId] = useState(tx.contractorId || '');
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false); // Stage 20g: отмена оплаты долга
  const [undoing, setUndoing] = useState(false);
  // (2026-06-10) если счёт операции в архиве — откат при архивации идёт на выбранный живой счёт.
  const _txAcc = (data.accounts || []).find(a => a.id === tx.accountId);
  const _txAccArchived = !!(_txAcc && _txAcc.archived);
  const _liveAccs = (data.accounts || []).filter(a => !a.archived && !a.isHidden);
  const [archiveTarget, setArchiveTarget] = useState(() => {
    const la = (data.accounts || []).filter(a => !a.archived && !a.isHidden);
    return la[0] ? la[0].id : '';
  });
  // R3 (2026-06-12): режим архивации операции мёртвого счёта.
  // 'skip' — без движения денег (деньги уже погашены writeoff'ом при удалении счёта, default);
  // 'revert' — откат на выбранный живой счёт (операция была ошибочной).
  const [archiveMode, setArchiveMode] = useState('skip');

  const contractors = (data.contractors || []).filter(c => !c.archived);
  const typeColor = tx.type === 'income' ? t.accentGreen : tx.type === 'expense' ? t.accentRed : t.accentCyan;
  const typeLabel = tx.type === 'income' ? S().data.txTypeIncome : tx.type === 'expense' ? S().data.txTypeExpense : S().data.txTypeTransfer;
  const accName = data.accounts.find(a => a.id === tx.accountId)?.name || '—';
  const fundDisplayName = tx.fundName || (data.funds || []).find(f => f.id === tx.fundId)?.name || '';
  const contractorDisplayName = (data.contractors || []).find(c => c.id === (contractorId || tx.contractorId))?.name || tx.contractorName || '';

  // O5 (2026-06-10): sequential — await PUT, при успехе reload-org. Правка метаданных операции
  // (комментарий/категория/контрагент); баланс и Operation.fundId backend НЕ меняет (Чанк A.2).
  const save = async () => {
    if (!apiSync || !apiSync.syncUpdateOperation) { onClose(); return; }
    const payload = { description: comment, contractorId: contractorId || null };
    // category шлём только для income/expense (для transfer catId='' затёр бы значение).
    if (tx.type === 'income' || tx.type === 'expense') {
      payload.category = catId || null;
    }
    let result = null;
    try { result = await apiSync.syncUpdateOperation(tx.id, payload); } catch (e) { result = null; }
    if (!result) return; // handleSyncError показал alert — модал оставляем для повтора
    onClose();
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  // O5 (2026-06-10): sequential — await PUT archived, backend откатывает баланс/фонды в той же
  // tx, потом reload-org (серверная правда). Оптимистичный revert убран.
  const archiveTx = async (revertAccountId) => {
    if (!apiSync || !apiSync.syncUpdateOperation) { onClose(); return; }
    const payload = { archived: true };
    // R3 (2026-06-12): для операции мёртвого счёта юзер выбирает режим.
    // 'skip' → skipBalanceRevert (R1 backend): архив записи без движения денег.
    // 'revert' → откат на выбранный живой счёт (иначе деньги вернулись бы на
    // невидимый архивный счёт → кассовый разрыв).
    if (_txAccArchived) {
      if (archiveMode === 'revert') {
        if (!revertAccountId) { alert('Выберите счёт для зачисления отката.'); return; }
        payload.revertAccountId = revertAccountId;
      } else {
        payload.skipBalanceRevert = true;
      }
    }
    let result = null;
    try { result = await apiSync.syncUpdateOperation(tx.id, payload); } catch (e) { result = null; }
    if (!result) return;
    onClose();
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  // Stage 20g (2026-06-08): отмена оплаты долга — атомарно на backend (revert денег + долга +
  // удаление операции), потом полный reload из БД (операция/долг/балансы освежаются разом).
  const undoDebtPayment = async () => {
    if (!apiSync || !apiSync.syncUndoDebtPayment) return;
    setUndoing(true);
    try {
      await apiSync.syncUndoDebtPayment(tx.id);
      // Stage 20g: мягкая перезагрузка орг (не full reload — иначе сбрасывает на выбор орг).
      try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
      onClose();
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, borderRadius: 16, padding: 24, width: 440, maxWidth: '95vw', border: `1px solid ${t.border}`, boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        {confirmArchive ? (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{S().data.moveToArchiveTitle}</div>
            {_txAccArchived ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: t.accentOrange, marginBottom: 10 }}>Счёт этой операции удалён или в архиве. Что сделать с деньгами ({fmt(tx.amount)} ₸)?</div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                  <input type="radio" name="archMode" checked={archiveMode === 'skip'} onChange={() => setArchiveMode('skip')} style={{ marginTop: 2 }} />
                  <span style={{ fontSize: 13, color: t.text, fontWeight: 600 }}>Без движения денег
                    <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 400 }}>Деньги уже были учтены при удалении счёта. Архивируется только запись, балансы не меняются.</div>
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: _liveAccs.length === 0 ? 'not-allowed' : 'pointer', opacity: _liveAccs.length === 0 ? 0.5 : 1 }}>
                  <input type="radio" name="archMode" disabled={_liveAccs.length === 0} checked={archiveMode === 'revert'} onChange={() => setArchiveMode('revert')} style={{ marginTop: 2 }} />
                  <span style={{ fontSize: 13, color: t.text, fontWeight: 600 }}>Операция была ошибочной — откатить
                    <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 400 }}>{tx.type === 'income' ? `${fmt(tx.amount)} ₸ будут списаны с выбранного счёта и фондов.` : `${fmt(tx.amount)} ₸ вернутся на выбранный счёт и фонды.`}</div>
                  </span>
                </label>
                {archiveMode === 'revert' && _liveAccs.length > 0 && (
                  <select value={archiveTarget} onChange={e => setArchiveTarget(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 14 }}>
                    {_liveAccs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>{S().data.moveToArchiveDesc}</div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => archiveTx(_txAccArchived && archiveMode === 'revert' ? archiveTarget : undefined)} style={{ flex: 1, padding: 10, borderRadius: 8, background: `${t.accentOrange}15`, color: t.accentOrange, fontWeight: 700, border: 'none', cursor: 'pointer' }}>{S().data.yesToArchive}</button>
              <button onClick={() => setConfirmArchive(false)} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.bgInput, color: t.text, fontWeight: 700, border: 'none', cursor: 'pointer' }}>{S().common.cancel}</button>
            </div>
          </div>
        ) : confirmUndo ? (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Восстановить долг и удалить оплату?</div>
            <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>Поступление будет удалено, сумма вернётся на счёт и фонды, а долг восстановится в прежнем виде. Страница обновится.</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={undoDebtPayment} disabled={undoing} style={{ flex: 1, padding: 10, borderRadius: 8, background: `${t.accentRed}15`, color: t.accentRed, fontWeight: 700, border: 'none', cursor: undoing ? 'wait' : 'pointer', opacity: undoing ? 0.7 : 1 }}>{undoing ? 'Восстановление...' : 'Да, восстановить долг'}</button>
              <button onClick={() => setConfirmUndo(false)} disabled={undoing} style={{ flex: 1, padding: 10, borderRadius: 8, background: t.bgInput, color: t.text, fontWeight: 700, border: 'none', cursor: 'pointer' }}>{S().common.cancel}</button>
            </div>
          </div>
        ) : (
          <>
            {/* ШАПКА — единый стиль */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{S().data.viewOperation}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <HelpTooltip text={S().data.txEditHelp} size={15} />
                <button onClick={() => setConfirmArchive(true)} title={S().data.toArchiveTooltip} style={{ background: t.bgInput, color: t.textMuted, padding: 6, borderRadius: 8, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accentOrange} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><Archive size={16} /></button>
                {tx.debtId && tx.type === 'income' && (
                  <button onClick={() => setConfirmUndo(true)} title="Восстановить долг и удалить оплату" style={{ background: t.bgInput, color: t.textMuted, padding: 6, borderRadius: 8, border: 'none', cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.color = t.accentRed} onMouseLeave={e => e.currentTarget.style.color = t.textMuted}><RotateCcw size={16} /></button>
                )}
                <button onClick={onClose} title={S().data.closeTooltip} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
              </div>
            </div>

            {/* ИНФО: тип + сумма + счёт (READ ONLY) */}
            <div style={{ padding: 14, background: `${typeColor}08`, borderRadius: 12, border: `1px solid ${typeColor}20`, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 14, fontWeight: 700, background: `${typeColor}14`, color: typeColor }}>{typeLabel}</span>
                <span className="mono" style={{ fontSize: 20, fontWeight: 800, color: typeColor, whiteSpace: 'nowrap' }}>
                  {tx.type === 'income' ? '+' : tx.type === 'transfer' ? '↔' : '−'}{fmt(tx.amount)}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 12, fontSize: 12, color: t.textMuted, flexWrap: 'wrap' }}>
                <span>💳 {accName}</span>
                {tx.type === 'expense' ? <span>📊 {fundDisplayName || '—'}</span> : (fundDisplayName && <span>📊 {fundDisplayName}</span>)}
                {contractorDisplayName && <span>👤 {contractorDisplayName}</span>}
                <span>📅 {tx.date ? new Date(tx.date).toLocaleDateString(getLocale()) : '—'}</span>
              </div>
            </div>

            {/* КОНТРАГЕНТ */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().data.contractor}</label>
              <select value={contractorId} onChange={e => setContractorId(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13 }}>
                <option value="">{S().data.noContractor}</option>
                {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* КАТЕГОРИЯ */}
            {tx.type !== 'transfer' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().data.categorySelectLabel}</label>
                <select value={catId} onChange={e => setCatId(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13 }}>
                  <option value="">{S().data.noCategory}</option>
                  {tx.type === 'income'
                    ? (data.incomeCategories || []).filter(c => !c.archived).map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                    : (data.expenseCategories || []).filter(c => !c.archived).map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                  }
                </select>
              </div>
            )}

            {/* КОММЕНТАРИЙ */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().data.commentLabel}</label>
              <input value={comment} onChange={e => setComment(e.target.value)} placeholder={S().data.descPlaceholder} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 13 }} />
            </div>

            {/* КНОПКИ */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={save} style={{ flex: 1, padding: 12, borderRadius: 8, background: t.accentGreen, color: '#fff', fontWeight: 700, border: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, cursor: 'pointer' }}><Check size={18} /> {S().common.save}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Удалена функция DataArchive, заменена на GlobalArchive из ArchiveTab

// === ОБНОВЛЕННЫЙ КОМПОНЕНТ "СЧЕТА И ФОНДЫ" ДЛЯ СПРАВОЧНИКА (С глобальной сортировкой) ===
function AccountsFundsTab({ data, uOrg, isCompact, t, user, perms, apiSync }) {
  const [activeAcc, setActiveAcc] = useState(null);
  const [activeFund, setActiveFund] = useState(null);
  const [editAcc, setEditAcc] = useState(null);
  const [editFund, setEditFund] = useState(null);
  const [qIncAcc, setQIncAcc] = useState(null);
  const [qExpFund, setQExpFund] = useState(null);
  // НОВОЕ: Стейт для вызова окна пополнения/изъятия скрытого счета
  const [qHiddenAcc, setQHiddenAcc] = useState(null);
  const [hist, setHist] = useState(null);
  // showHelp state removed — now using HelpTooltip component

  const canAcc = perms.all || perms.manageAccounts;
  const canFund = perms.all || perms.manageFunds;
  const canOps = perms.all || perms.manageOperations;

  const accountsActive = (data.accounts || []).filter(a => !a.archived);
  const fundsActive = (data.funds || []).filter(f => !f.archived);
  const tAcc = accountsActive.filter(a => !a.isHidden).reduce((s, a) => s + (data.accountBalances[a.id] || 0), 0);
  const tFund = fundsActive.reduce((s, f) => s + (data.fundBalances[f.id] || 0), 0);

  // Глобальная функция сортировки (меняет порядок в самой базе данных)
  const applySort = (type, sortOption) => {
    const items = type === 'acc' ? data.accounts : data.funds;
    const active = [...items].filter(i => !i.archived);
    const archived = items.filter(i => i.archived);

    active.sort((a, b) => {
      // Для счетов жестко держим Основной сверху, а Скрытые снизу, внутри них сортируем
      if (type === 'acc') {
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        if (a.isHidden && !b.isHidden) return 1;
        if (!a.isHidden && b.isHidden) return -1;
      }

      // Сама логика сортировки
      if (sortOption === 'alpha') return a.name.localeCompare(b.name);
      if (sortOption === 'bal_desc') {
        const balA = type === 'acc' ? (data.accountBalances[a.id] || 0) : (data.fundBalances[a.id] || 0);
        const balB = type === 'acc' ? (data.accountBalances[b.id] || 0) : (data.fundBalances[b.id] || 0);
        return balB - balA;
      }
      if (sortOption === 'bal_asc') {
        const balA = type === 'acc' ? (data.accountBalances[a.id] || 0) : (data.fundBalances[a.id] || 0);
        const balB = type === 'acc' ? (data.accountBalances[b.id] || 0) : (data.fundBalances[b.id] || 0);
        return balA - balB;
      }
      if (sortOption === 'pct_desc' && type === 'fund') return (b.percent || 0) - (a.percent || 0);
      return 0;
    });

    uOrg(p => ({ ...p, [type === 'acc' ? 'accounts' : 'funds']: [...active, ...archived] }));
  };

  // Ручная перестановка элементов (Свой порядок)
  const moveItem = (item, dir, type) => {
    const items = type === 'acc' ? data.accounts : data.funds;
    const active = [...items].filter(i => !i.archived);
    const archived = items.filter(i => i.archived);

    const idx = active.findIndex(x => x.id === item.id);
    if (idx < 0) return;

    let targetIdx = -1;
    // Ищем ближайшего соседа по группе (чтобы обычные счета не перемешались со скрытыми)
    if (dir === 'up') {
      for (let i = idx - 1; i >= 0; i--) {
        if (type === 'acc' && active[i].isPrimary === item.isPrimary && active[i].isHidden === item.isHidden) { targetIdx = i; break; }
        if (type === 'fund') { targetIdx = i; break; }
      }
    } else {
      for (let i = idx + 1; i < active.length; i++) {
        if (type === 'acc' && active[i].isPrimary === item.isPrimary && active[i].isHidden === item.isHidden) { targetIdx = i; break; }
        if (type === 'fund') { targetIdx = i; break; }
      }
    }

    if (targetIdx !== -1) {
      [active[idx], active[targetIdx]] = [active[targetIdx], active[idx]];
      uOrg(p => ({ ...p, [type === 'acc' ? 'accounts' : 'funds']: [...active, ...archived] }));
    }
  };

  // Визуальный порядок для рендера (страхует группировку)
  const sortedAccs = [...accountsActive].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    if (a.isHidden && !b.isHidden) return 1;
    if (!a.isHidden && b.isHidden) return -1;
    return accountsActive.indexOf(a) - accountsActive.indexOf(b); // сохраняем порядок массива
  });
  const sortedFunds = [...fundsActive];

  // O6 + фича (2026-06-10): sequential + авто-распределение стартового баланса по фондам.
  const saveAcc = async () => {
    if (!editAcc.name.trim()) { alert(S().data.enterAccountName); return; }
    if (!apiSync) { alert((S().common && S().common.unknownError) || 'Нет связи с сервером'); return; }
    const isNew = !editAcc.id;
    const newId = isNew ? `acc_${uid()}` : editAcc.id;
    const startBal = isNew ? (parseFloat(editAcc.balance) || 0) : 0;
    const activeFunds = (data.funds || []).filter(f => !f.archived);
    const willDistribute = isNew && !editAcc.isHidden && startBal > 0 && activeFunds.length > 0;
    let result = null;
    try {
      if (isNew) {
        result = await apiSync.syncCreateAccount({
          id: newId,
          name: editAcc.name,
          type: editAcc.type || 'cash',
          balance: willDistribute ? 0 : startBal,
          color: editAcc.color || null,
          isHidden: !!editAcc.isHidden,
          isPrimary: !!editAcc.isPrimary,
        });
      } else {
        result = await apiSync.syncUpdateAccount(editAcc.id, { name: editAcc.name, type: editAcc.type });
      }
    } catch (e) { result = null; }
    if (!result) return; // handleSyncError показал alert — модал оставляем для повтора
    if (willDistribute) {
      const bd = [];
      activeFunds.forEach((f, idx) => {
        let pt = Math.round(startBal * f.percent) / 100;
        if (idx === activeFunds.length - 1) {
          const allocated = bd.reduce((s, b) => s + b.amount, 0);
          pt = Math.round((startBal - allocated) * 100) / 100;
        }
        bd.push({ fundId: f.id, fundName: f.name, amount: pt });
      });
      try {
        await apiSync.syncCreateOperation({
          id: `op_${uid()}`, type: 'income', accountId: newId, amount: startBal,
          comment: `Пополнение при создании счёта «${editAcc.name}» с балансом ${fmt(startBal)}`,
          breakdown: bd, incomeCategoryId: null,
          userId: user?.id, userName: user?.name, date: new Date().toISOString(),
        });
      } catch (e) {}
    }
    setEditAcc(null);
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  const saveFund = async () => {
    if (!editFund.name.trim()) { alert(S().data.enterFundName); return; }
    const isNew = !editFund.id;
    const newId = isNew ? `fund_${uid()}` : editFund.id;
    // F3 (2026-06-08): single-source — БЕЗ оптимистичной отрисовки. Ждём backend →
    // событие refetch → UI из БД (как F2, см. feedback_fund_meta_dirty_tracking_fragile).
    if (apiSync) {
      const pct = parseFloat(editFund.percent) || 0;
      if (isNew) {
        if (apiSync.syncCreateFund) await apiSync.syncCreateFund({ id: newId, name: editFund.name, percent: pct, color: editFund.color || null, balance: 0 });
      } else {
        if (apiSync.syncUpdateFund) await apiSync.syncUpdateFund(newId, { name: editFund.name, percent: pct, color: editFund.color || null });
      }
      try { window.dispatchEvent(new CustomEvent('inmonex-refetch-funds')); } catch (e) {}
    }
    setEditFund(null);
  };

  // Чанк J (2026-05-19): расширено для атомарного transfer balance при archive.
  // Если balance != 0 — показываем Confirm с select target. Backend atomic endpoint
  // переносит balance + archive в одной Prisma transaction (защита от orphan).
  const archAcc = id => {
    const a = data.accounts.find(x => x.id === id);
    if (!a) return;
    const accBalance = parseFloat(data.accountBalances[id] || a.balance || 0) || 0;
    if (accBalance === 0) {
      // Простой archive без transfer
      uOrg(p => ({ ...p, accounts: p.accounts.map(x => x.id === id ? { ...x, archived: true } : x) }));
      if (apiSync) apiSync.syncUpdateAccount(id, { isArchived: true });
      setActiveAcc(null);
      return;
    }
    // Balance > 0 — нужен transfer
    const candidates = (data.accounts || []).filter(x =>
      !x.archived && !x.isHidden && x.id !== id
    );
    if (candidates.length === 0) {
      window.alert(`Нельзя архивировать счёт "${a.name}" — нет активных счетов для переноса ${fmt(accBalance)} ₸.`);
      return;
    }
    setConfirmArchiveAcc({
      accId: id,
      accName: a.name,
      accBalance,
      candidates,
      target: candidates[0].id,
    });
  };

  const doArchiveAccountWithTransfer = () => {
    if (!confirmArchiveAcc) return;
    const { accId, accName, accBalance, candidates, target } = confirmArchiveAcc;
    const targetAcc = candidates.find(c => c.id === target);
    if (!targetAcc) {
      window.alert('Целевой счёт не найден.');
      return;
    }
    // Чанк J review BLOCKER 2: await-based вместо optimistic — safer для finance.
    // Local state применяется ТОЛЬКО после backend confirmation. handleSyncError
    // показывает alert при 4xx/5xx и не trigger.then.
    if (!apiSync || !apiSync.syncArchiveAccountWithTransfer) {
      window.alert('apiSync недоступен. Обновите страницу.');
      return;
    }
    apiSync.syncArchiveAccountWithTransfer(accId, { balanceTarget: target })
      .then(result => {
        if (!result) return;  // handleSyncError уже показал alert
        uOrg(p => ({
          ...p,
          accounts: p.accounts.map(x => x.id === accId ? { ...x, archived: true } : x),
          accountBalances: {
            ...p.accountBalances,
            [accId]: 0,
            [target]: (parseFloat(p.accountBalances[target]) || 0) + accBalance,
          },
        }));
        addAuditLog(uOrg, user, {
          type: 'acc_archive',
          desc: `Счёт "${accName}" архивирован с переносом ${fmt(accBalance)} ₸ → "${targetAcc.name}"`,
          before: { name: accName, balance: accBalance },
          after: { archived: true, balanceTarget: targetAcc.name },
        });
        setConfirmArchiveAcc(null);
        setActiveAcc(null);
      });
  };
  const archFund = async id => { if (apiSync && apiSync.syncArchiveFund) await apiSync.syncArchiveFund(id); try { window.dispatchEvent(new CustomEvent('inmonex-refetch-funds')); } catch (e) {} setActiveFund(null); };

  // Чанк J (2026-05-19): state для модалки выбора target при archive с balance != 0
  // B+C (2026-06-10): удаление счёта «вместе с деньгами» — расход на сумму баланса по фондам
  // (computeFundWriteoff: по %, недостача каскадом на крупнейший) → счёт 0 + фонды −X атомарно,
  // затем архив. Балансы фондов берём из data.fundBalances.
  const doDeleteAccountWithFundWriteoff = async (id, accName, accBalance) => {
    if (!apiSync || !apiSync.syncCreateOperation) { window.alert('apiSync недоступен. Обновите страницу.'); return; }
    const X = Math.round((accBalance || 0) * 100) / 100;
    if (X <= 0) {
      if (apiSync.syncUpdateAccount) { try { await apiSync.syncUpdateAccount(id, { isArchived: true }); } catch (e) {} }
      setConfirmArchiveAcc(null); setActiveAcc(null);
      try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
      return;
    }
    const fb = data.fundBalances || {};
    const activeFunds = (data.funds || []).filter(f => !f.archived).map(f => ({
      id: f.id, name: f.name, percent: f.percent,
      balance: Math.round(((fb[f.id] != null ? fb[f.id] : (f.balance || 0))) * 100) / 100,
    }));
    if (activeFunds.length === 0) { window.alert('Нет активных фондов для списания.'); return; }
    const { breakdown, remaining } = computeFundWriteoff(X, activeFunds);
    if (Math.abs(remaining) > 0.01) { window.alert('В фондах недостаточно средств для списания этой суммы. Операция отменена.'); return; }
    const expRes = await apiSync.syncCreateOperation({
      id: `op_${uid()}`, type: 'expense', accountId: id, amount: X,
      comment: `Списание при удалении счёта «${accName}»`,
      breakdown, expenseCategoryId: null,
      userId: user?.id, userName: user?.name, date: new Date().toISOString(),
    });
    if (!expRes) return;
    if (apiSync.syncUpdateAccount) { try { await apiSync.syncUpdateAccount(id, { isArchived: true }); } catch (e) {} }
    addAuditLog(uOrg, user, { type: 'acc_archive', desc: `Счёт «${accName}» удалён со списанием ${fmt(X)} ₸ с фондов`, after: { archived: true, writeoff: X } });
    setConfirmArchiveAcc(null); setActiveAcc(null);
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  const [confirmArchiveAcc, setConfirmArchiveAcc] = useState(null);

  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

  return (
    <div>
      {hist && <HistoryModal data={data} targetId={hist.id} targetName={hist.name} onClose={() => setHist(null)} onNavigate={() => { }} />}

      {/* Модалки быстрого пополнения/расхода */}
      {qIncAcc && <QuickIncomeModal data={data} uOrg={uOrg} user={user} accId={qIncAcc} onClose={() => setQIncAcc(null)} apiSync={apiSync} />}
      {qExpFund && <QuickExpenseModal data={data} uOrg={uOrg} user={user} aAccs={(data.accounts || []).filter(a => !a.archived && !a.isHidden)} fundId={qExpFund} onClose={() => setQExpFund(null)} apiSync={apiSync} />}
      {/* Вызов модалки для скрытых счетов */}
      {qHiddenAcc && <QuickHiddenAdjustModal data={data} uOrg={uOrg} user={user} apiSync={apiSync} accId={qHiddenAcc.id} type={qHiddenAcc.type} onClose={() => setQHiddenAcc(null)} />}
      {/* МОДАЛКА ДОБАВЛЕНИЯ СЧЕТА (Без изменений, она у нас отличная) */}
      {editAcc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (() => { setEditAcc(null); })(); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `2px solid ${editAcc.color}`, borderRadius: 16, padding: 24, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: editAcc.color }}>{S().data.newAccount}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <HelpTooltip text={HELP_TEXTS.setupAccount || S().data.accountHelpFallback} size={15} />
                <button onClick={() => { setEditAcc(null); }} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
              </div>
            </div>

            <div style={{ display: 'grid', gap: 14, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().data.accountNameLabel}</label>
                <input value={editAcc.name} onChange={e => setEditAcc({ ...editAcc, name: e.target.value })} placeholder={S().data.accountNamePlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} />
              </div>

              {/* Настройки счета */}
              <div style={{ padding: 14, background: t.bgInput, borderRadius: 12, border: `1px solid ${t.border}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={editAcc.isPrimary} onChange={e => setEditAcc({ ...editAcc, isPrimary: e.target.checked })} style={{ width: 16, height: 16 }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{S().data.makePrimary}</span>
                  </label>
                  <HelpTooltip text={HELP_TEXTS.primaryAccount} size={14} />
                </div>


                <div style={{ height: 1, background: t.border, marginBottom: 12 }} />

                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={editAcc.isHidden} onChange={e => setEditAcc({ ...editAcc, isHidden: e.target.checked })} style={{ width: 16, height: 16 }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{S().data.hiddenAccountLabel}</span>
                  </label>
                  <HelpTooltip text={HELP_TEXTS.hiddenAccount} size={14} />
                </div>


                {editAcc.isHidden && (
                  <div style={{ marginTop: 12, padding: 10, background: t.bgCard, borderRadius: 8, border: `1px solid ${t.border}` }}>
                    <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><Eye size={14} /> {S().data.whoCanSee}</label>
                    <select value={editAcc.visibleTo || 'me'} onChange={e => setEditAcc({ ...editAcc, visibleTo: e.target.value })} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 13 }}>
                      <option value="me">{S().data.visibleOnlyMe}</option>
                      <option value="me_accountant">{S().data.visibleMeAccountant}</option>
                      <option value="admins">{S().data.visibleAdmins}</option>
                      <option value="all">{S().data.visibleAll}</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>
                  {S().data.ibanLabel}
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input value={editAcc.iban || ''} onChange={e => setEditAcc({ ...editAcc, iban: e.target.value })} placeholder="KZ..." style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} />
                  <HelpTooltip text={S().data.ibanHelpTooltip} size={16} />
                </div>
              </div>

              {!editAcc.id && (
                <div>
                  <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>
                    Стартовый баланс ₸
                  </label>
                  <input
                    type="number"
                    value={editAcc.balance ?? ''}
                    onChange={e => setEditAcc({ ...editAcc, balance: e.target.value })}
                    placeholder="0"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }}
                  />
                </div>
              )}

              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 8, display: 'block' }}>{S().data.colorLabel}</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {colors.map(c => (
                    <button key={c} onClick={() => setEditAcc({ ...editAcc, color: c })} style={{ width: 32, height: 32, borderRadius: '50%', background: c, border: editAcc.color === c ? `3px solid ${t.text}` : 'none', cursor: 'pointer' }} />
                  ))}
                </div>
              </div>
            </div>
            <Btn variant="success" style={{ width: '100%', justifyContent: 'center', padding: 12 }} onClick={() => {
              if (!editAcc.name.trim()) return alert(S().data.enterAccountName);
              if (!editAcc.iban?.trim()) return alert(S().data.ibanRequired);
              saveAcc();
            }}><Check size={18} /> {S().data.saveAccount}</Btn>
          </div>
        </div>
      )}

      {/* МОДАЛКА ДОБАВЛЕНИЯ ФОНДА (Убрали галочку Основной) */}
      {editFund && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (() => { setEditFund(null); })(); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `2px solid ${editFund.color}`, borderRadius: 16, padding: 24, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: editFund.color }}>{S().data.newFund}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <HelpTooltip text={HELP_TEXTS.setupFund || S().data.fundHelpFallback} size={15} />
                <button onClick={() => { setEditFund(null); }} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
              </div>
            </div>

            <div style={{ display: 'grid', gap: 14, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().data.fundNameLabel}</label>
                <input value={editFund.name} onChange={e => setEditFund({ ...editFund, name: e.target.value })} placeholder={S().data.fundNamePlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().data.fundPercentLabel}</label>
                  <input type="number" value={editFund.percent} onChange={e => setEditFund({ ...editFund, percent: parseFloat(e.target.value) || 0 })} placeholder="10" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().data.fundTargetLabel}</label>
                  <input type="number" value={editFund.target || ''} onChange={e => setEditFund({ ...editFund, target: parseFloat(e.target.value) || '' })} placeholder="1 000 000" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} />
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 8, display: 'block' }}>{S().data.colorLabel}</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {colors.map(c => (
                    <button key={c} onClick={() => setEditFund({ ...editFund, color: c })} style={{ width: 32, height: 32, borderRadius: '50%', background: c, border: editFund.color === c ? `3px solid ${t.text}` : 'none', cursor: 'pointer' }} />
                  ))}
                </div>
              </div>
            </div>
            <Btn variant="success" style={{ width: '100%', justifyContent: 'center', padding: 12 }} onClick={saveFund}><Check size={18} /> {S().data.saveFund}</Btn>
          </div>
        </div>
      )}

      {/* ОРИГИНАЛЬНЫЕ МОДАЛКИ ПРОСМОТРА */}
      {activeAcc && <AccountModal acc={activeAcc} data={data} uOrg={uOrg} t={t} apiSync={apiSync} onClose={() => setActiveAcc(null)} onArchive={() => archAcc(activeAcc.id)} onShowHistory={() => { setHist({ id: activeAcc.id, name: activeAcc.name }); setActiveAcc(null); }} />}

      {/* Чанк J (2026-05-19): модалка выбора target счёта при archive с balance != 0 */}
      {confirmArchiveAcc && <Confirm
        msg={`Архивировать счёт "${confirmArchiveAcc.accName}" (${fmt(confirmArchiveAcc.accBalance)} ₸)?`}
        extra={
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 10 }}>
              Куда перенести {fmt(confirmArchiveAcc.accBalance)} ₸?
            </div>
            <select
              value={confirmArchiveAcc.target}
              onChange={e => setConfirmArchiveAcc(p => ({ ...p, target: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, fontSize: 14 }}
            >
              {confirmArchiveAcc.candidates.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} ({fmt(parseFloat(data.accountBalances[c.id] || c.balance || 0))} ₸)
                </option>
              ))}
            </select>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 8 }}>или удалить деньги — списать с фондов (по %, остаток с крупнейшего):</div>
              <button
                onClick={() => { const cf = confirmArchiveAcc; setConfirmArchiveAcc(null); doDeleteAccountWithFundWriteoff(cf.accId, cf.accName, cf.accBalance); }}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: `${t.accentRed}15`, color: t.accentRed, fontWeight: 700, border: `1px solid ${t.accentRed}40`, cursor: 'pointer' }}
              >
                Удалить вместе с деньгами
              </button>
            </div>
          </div>
        }
        onYes={doArchiveAccountWithTransfer}
        onNo={() => setConfirmArchiveAcc(null)}
      />}
      {activeFund && <FundModal fund={activeFund} data={data} uOrg={uOrg} t={t} tPct={fundsActive.reduce((s, f) => s + f.percent, 0)} apiSync={apiSync} onClose={() => setActiveFund(null)} onArchive={() => archFund(activeFund.id)} onShowHistory={() => { setHist({ id: activeFund.id, name: activeFund.name }); setActiveFund(null); }} />}

      {/* СПИСКИ СЧЕТОВ И ФОНДОВ (Добавлена сортировка и стрелочки) */}
      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 24 }}>

        {/* ЛЕВАЯ КОЛОНКА: СЧЕТА */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: t.text }}><CreditCard size={20} color={t.accent} /> {S().data.yourAccounts}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select onChange={e => { if (e.target.value) applySort('acc', e.target.value); e.target.value = ''; }} style={{ padding: '6px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12 }}>
                <option value="">{S().data.sortLabel}</option>
                <option value="alpha">{S().data.sortAlpha}</option>
                <option value="bal_desc">{S().data.sortBalDesc}</option>
                <option value="bal_asc">{S().data.sortBalAsc}</option>
              </select>
              {canAcc && <Btn variant="primary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setEditAcc({ name: '', color: '#3b82f6', isPrimary: false, isHidden: false, visibleTo: 'me', balance: '' })}><Plus size={14} /> {S().data.addBtn}</Btn>}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedAccs.length === 0 ? <div style={{ textAlign: 'center', padding: 20, color: t.textMuted, fontSize: 13, border: `1px dashed ${t.border}`, borderRadius: 12 }}>{S().data.noActiveAccounts}</div> : sortedAccs.map(a => {
              const bal = data.accountBalances[a.id] || 0;
              return (
                <div key={a.id} onClick={() => setActiveAcc(a)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}`, borderLeft: `4px solid ${a.color}`, cursor: 'pointer', transition: '0.2s', opacity: a.isHidden ? 0.8 : 1 }} onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.05)'} onMouseLeave={e => e.currentTarget.style.filter = 'none'}>

                  {/* Кнопки вверх/вниз для ручной сортировки */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0, background: t.bgCard, borderRadius: 6, padding: 2, border: `1px solid ${t.border}` }}>
                    <button onClick={(e) => { e.stopPropagation(); moveItem(a, 'up', 'acc'); }} style={{ background: 'transparent', border: 'none', color: t.textMuted, cursor: 'pointer', padding: '0 2px' }}><ChevronUp size={14} /></button>
                    <button onClick={(e) => { e.stopPropagation(); moveItem(a, 'down', 'acc'); }} style={{ background: 'transparent', border: 'none', color: t.textMuted, cursor: 'pointer', padding: '0 2px' }}><ChevronDown size={14} /></button>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{a.name}</div>
                    {a.isPrimary && <span style={{ fontSize: 11, background: `${t.accent}15`, color: t.accent, padding: '2px 6px', borderRadius: 6, fontWeight: 700 }}>{S().data.primaryBadge}</span>}
                    {a.isHidden && <EyeOff size={14} color={t.textMuted} title={S().data.hiddenAccountTooltip} />}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <div className="mono" style={{ fontSize: 14, fontWeight: 800, color: bal > 0 ? a.color : t.textSec, marginRight: 6 }}>{fmt(bal)}</div>

                    {/* НОВОЕ: Если счет скрытый, показываем + и - для заначки, иначе стандартный + */}
                    {a.isHidden ? (
                      canOps && (
                        <>
                          <button onClick={(e) => { e.stopPropagation(); setQHiddenAcc({ id: a.id, type: 'income' }); }} title={S().data.topUpStash} style={{ background: 'transparent', color: t.accentGreen, padding: 3, border: 'none', cursor: 'pointer' }}><Plus size={15} /></button>
                          <button onClick={(e) => { e.stopPropagation(); setQHiddenAcc({ id: a.id, type: 'expense' }); }} title={S().data.withdrawStash} style={{ background: 'transparent', color: t.accentRed, padding: 3, border: 'none', cursor: 'pointer' }}><Minus size={15} /></button>
                        </>
                      )
                    ) : (
                      canOps && <button onClick={(e) => { e.stopPropagation(); setQIncAcc(a.id); }} title={S().data.quickIncomeTooltip} style={{ background: 'transparent', color: t.accentGreen, padding: 3, border: 'none', cursor: 'pointer' }}><Plus size={15} /></button>
                    )}

                    <button onClick={(e) => { e.stopPropagation(); setHist({ id: a.id, name: a.name }); }} title={S().data.historyBtn} style={{ background: 'transparent', color: t.accentCyan, padding: 3, border: 'none', cursor: 'pointer' }}><History size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ПРАВАЯ КОЛОНКА: ФОНДЫ */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: t.text }}><FolderOpen size={20} color={t.accentPurple} /> {S().data.fundsTitle}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select onChange={e => { if (e.target.value) applySort('fund', e.target.value); e.target.value = ''; }} style={{ padding: '6px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12 }}>
                <option value="">{S().data.sortLabel}</option>
                <option value="alpha">{S().data.sortAlpha}</option>
                <option value="bal_desc">{S().data.sortBalDesc}</option>
                <option value="bal_asc">{S().data.sortBalAsc}</option>
                <option value="pct_desc">{S().data.sortPctDesc}</option>
              </select>
              {canFund && <Btn variant="primary" style={{ padding: '6px 12px', fontSize: 12, background: t.accentPurple, borderColor: t.accentPurple }} onClick={() => setEditFund({ name: '', target: '', percent: 0, color: '#8b5cf6' })}><Plus size={14} /> {S().data.addBtn}</Btn>}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedFunds.length === 0 ? <div style={{ textAlign: 'center', padding: 20, color: t.textMuted, fontSize: 13, border: `1px dashed ${t.border}`, borderRadius: 12 }}>{S().data.noActiveFunds}</div> : sortedFunds.map(f => {
              const bal = data.fundBalances[f.id] || 0;
              return (
                <div key={f.id} onClick={() => setActiveFund(f)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}`, borderLeft: `4px solid ${f.color}`, cursor: 'pointer', transition: '0.2s' }} onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.05)'} onMouseLeave={e => e.currentTarget.style.filter = 'none'}>

                  {/* Кнопки вверх/вниз */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0, background: t.bgCard, borderRadius: 6, padding: 2, border: `1px solid ${t.border}` }}>
                    <button onClick={(e) => { e.stopPropagation(); moveItem(f, 'up', 'fund'); }} style={{ background: 'transparent', border: 'none', color: t.textMuted, cursor: 'pointer', padding: '0 2px' }}><ChevronUp size={14} /></button>
                    <button onClick={(e) => { e.stopPropagation(); moveItem(f, 'down', 'fund'); }} style={{ background: 'transparent', border: 'none', color: t.textMuted, cursor: 'pointer', padding: '0 2px' }}><ChevronDown size={14} /></button>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{f.name}</div>
                    <div style={{ fontSize: 11, padding: '2px 6px', borderRadius: 6, background: `${f.color}18`, color: f.color, fontWeight: 700, flexShrink: 0 }}>{f.percent}%</div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <div className="mono" style={{ fontSize: 14, fontWeight: 800, color: bal > 0 ? f.color : t.textSec, marginRight: 6 }}>{fmt(bal)}</div>
                    {canOps && <button onClick={(e) => { e.stopPropagation(); setQExpFund(f.id); }} title={S().data.quickExpenseTooltip} style={{ background: 'transparent', color: t.accentRed, padding: 3, border: 'none', cursor: 'pointer' }}><Minus size={15} /></button>}
                    <button onClick={(e) => { e.stopPropagation(); setHist({ id: f.id, name: f.name }); }} title={S().data.historyBtn} style={{ background: 'transparent', color: t.accentCyan, padding: 3, border: 'none', cursor: 'pointer' }}><History size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

// === ОБНОВЛЕННАЯ МОДАЛКА СЧЕТА ДЛЯ СПРАВОЧНИКА (DataTab.js) ===
function AccountModal({ acc, data, uOrg, t, onClose, onArchive, onShowHistory, apiSync }) {
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState(acc.name);

  // НОВОЕ: Стейты для Основного, Скрытого счета и Видимости
  const [isPrimary, setIsPrimary] = useState(acc.isPrimary || false);
  const [isHidden, setIsHidden] = useState(acc.isHidden || false);
  const [visibleTo, setVisibleTo] = useState(acc.visibleTo || 'me');
  const [iban, setIban] = useState(acc.iban || '');

  const [showCPicker, setShowCPicker] = useState(false);
  const [showCatPicker, setShowCatPicker] = useState(false);

  const myContractors = (data.contractors || []).filter(c => !c.archived && c.isCustomer && (c.accountIds?.includes(acc.id) || !c.accountIds?.length));
  const availContractors = (data.contractors || []).filter(c => !c.archived && c.isCustomer && !c.accountIds?.includes(acc.id));

  const myCats = (data.incomeCategories || []).filter(c => !c.archived && c.accountId === acc.id);
  const availCats = (data.incomeCategories || []).filter(c => !c.archived && c.accountId !== acc.id);

  const toggleC = (cId) => {
    // Чанк 20a.5b: persist accountIds в БД через REST.
    const cur = (data.contractors || []).find(c => c.id === cId);
    if (!cur) return;
    const ids = cur.accountIds || [];
    const newAccountIds = ids.includes(acc.id) ? ids.filter(x => x !== acc.id) : [...ids, acc.id];
    uOrg(p => ({ ...p, contractors: p.contractors.map(c => c.id !== cId ? c : { ...c, accountIds: newAccountIds }) }));
    if (apiSync && apiSync.syncUpdateContractor) apiSync.syncUpdateContractor(cId, { accountIds: newAccountIds });
  };
  // Stage 20b B.3: REST PUT /categories.
  const toggleCat = (cId) => {
    const cur = (data.incomeCategories || []).find(c => c.id === cId);
    const newAccountId = cur && cur.accountId === acc.id ? '' : acc.id;
    uOrg(p => ({ ...p, incomeCategories: p.incomeCategories.map(c => c.id === cId ? { ...c, accountId: newAccountId } : c) }));
    if (apiSync && apiSync.syncUpdateCategory) apiSync.syncUpdateCategory(cId, { accountId: newAccountId });
  };

  const save = () => {
    if (!name.trim()) return alert(S().data.enterAccountName);
    if (!iban.trim()) return alert(S().data.enterIban);

    uOrg(p => ({
      ...p,
      accounts: p.accounts.map(a => {
        if (a.id === acc.id) return { ...a, name, isPrimary, isHidden, visibleTo, iban };
        if (isPrimary) return { ...a, isPrimary: false };
        return a;
      })
    }));
    setIsEdit(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, borderRadius: 20, width: 500, maxHeight: '90vh', padding: 24, display: 'flex', flexDirection: 'column', border: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setIsEdit(!isEdit)} style={{ background: t.bgInput, color: t.textSec, padding: 8, borderRadius: 8, border: `1px solid ${t.border}`, cursor: 'pointer' }}><Edit3 size={18} /></button>
            <button onClick={onArchive} style={{ background: `${t.accentOrange}15`, color: t.accentOrange, padding: 8, borderRadius: 8, border: 'none', cursor: 'pointer' }}><Archive size={18} /></button>
          </div>
          <button onClick={onClose} style={{ color: t.textMuted, background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <div style={{ overflowY: 'auto' }}>
          {isEdit ? (
            <div style={{ display: 'grid', gap: 12, marginBottom: 20, background: t.bgInput, padding: 14, borderRadius: 12 }}>
              <input value={name} onChange={e => setName(e.target.value)} style={{ fontSize: 16, fontWeight: 700, padding: 8, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text }} autoFocus />

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
                <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{S().data.primaryAccount}</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={isHidden} onChange={e => setIsHidden(e.target.checked)} style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{S().data.hiddenAccountLabel}</span>
              </label>

              {/* Настройка видимости */}
              {isHidden && (
                <div style={{ marginTop: 8, padding: 10, background: t.bgCard, borderRadius: 8, border: `1px solid ${t.border}` }}>
                  <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>{S().data.whoCanSee}</label>
                  <select value={visibleTo} onChange={e => setVisibleTo(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 13 }}>
                    <option value="me">{S().data.visibleOnlyMe}</option>
                    <option value="me_accountant">{S().data.visibleMeAccountant}</option>
                    <option value="admins">{S().data.visibleAdmins}</option>
                    <option value="all">{S().data.visibleAll}</option>
                  </select>
                </div>
              )}

              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {S().data.ibanLabel}
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input value={iban} onChange={e => setIban(e.target.value)} placeholder="KZ..." style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text }} />
                  <HelpTooltip text={S().data.ibanHelpTooltip} size={16} />
                </div>
              </div>

              <Btn variant="success" style={{ justifyContent: 'center', marginTop: 10 }} onClick={save}><Check size={18} /> {S().common.save}</Btn>
            </div>
          ) : (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontSize: 24, fontWeight: 800 }}>{acc.name}</h2>
                {acc.isPrimary && <span style={{ fontSize: 11, background: `${t.accent}15`, color: t.accent, padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>{S().data.primaryBadge}</span>}
                {acc.isHidden && <EyeOff size={18} color={t.textMuted} title={S().data.hiddenAccountTooltip} />}
              </div>

              {/* Вывод видимости */}
              {acc.isHidden && (
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <EyeOff size={14} />
                  {S().data.visibilityPrefix}{acc.visibleTo === 'me' ? S().data.visibleOnlyMe : acc.visibleTo === 'me_accountant' ? S().data.visibleMeAccountant : acc.visibleTo === 'admins' ? S().data.visibleAdmins : S().data.visibleAll}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <div style={{ padding: '4px 12px', background: `${t.accentGreen}15`, color: t.accentGreen, borderRadius: 20, fontSize: 13, fontWeight: 700 }}>{S().data.balancePrefix(fmt(data.accountBalances[acc.id] || 0))}</div>
                <button onClick={onShowHistory} style={{ color: t.accentCyan, background: 'transparent', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, border: 'none', cursor: 'pointer' }}><History size={14} /> {S().data.historyBtn}</button>
              </div>
              {acc.iban && (
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 8, padding: '8px 12px', background: t.bgInput, borderRadius: 8, border: `1px solid ${t.border}` }}>
                  <span style={{ fontWeight: 700 }}>{S().data.ibanShort}</span> {acc.iban}
                </div>
              )}
            </div>
          )}

          {!acc.isHidden && (
            <>
              <div style={{ marginBottom: 24, borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Tags size={16} color={t.accentGreen} /> {S().data.incomeCategories}</span>
                  <button onClick={() => setShowCatPicker(!showCatPicker)} style={{ color: t.accent, background: 'transparent', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer' }}><Plus size={14} /> {S().common.add}</button>
                </div>
                {showCatPicker && <div style={{ background: t.bgInput, padding: 10, borderRadius: 8, marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
                  {availCats.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().data.noFreeCategories}</div> : availCats.map(c => <div key={c.id} onClick={() => toggleCat(c.id)} style={{ padding: '8px', cursor: 'pointer', borderBottom: `1px solid ${t.border}`, fontSize: 13 }}>{c.name}</div>)}
                </div>}
                <div style={{ display: 'grid', gap: 6 }}>
                  {myCats.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().data.noLinkedCategories}</div> : myCats.map(c => <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: t.bgInput, borderRadius: 10 }}><span>{c.name}</span><button onClick={() => toggleCat(c.id)} style={{ color: t.accentRed, background: 'transparent', fontSize: 11, border: 'none', cursor: 'pointer' }}>{S().data.removeLink}</button></div>)}
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Users size={16} color={t.accent} /> {S().data.clientsTitle}</span>
                  <button onClick={() => setShowCPicker(!showCPicker)} style={{ color: t.accent, background: 'transparent', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer' }}><Plus size={14} /> {S().common.add}</button>
                </div>
                {showCPicker && <div style={{ background: t.bgInput, padding: 10, borderRadius: 8, marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
                  {availContractors.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().data.allClientsLinked}</div> : availContractors.map(c => <div key={c.id} onClick={() => toggleC(c.id)} style={{ padding: '8px', cursor: 'pointer', borderBottom: `1px solid ${t.border}`, fontSize: 13 }}>{c.name}</div>)}
                </div>}
                <div style={{ display: 'grid', gap: 6 }}>
                  {myContractors.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().data.noLinkedClients}</div> : myContractors.map(c => <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: t.bgInput, borderRadius: 10 }}><span>{c.name}</span><button onClick={() => toggleC(c.id)} style={{ color: t.accentRed, background: 'transparent', fontSize: 11, border: 'none', cursor: 'pointer' }}>{S().data.removeLink}</button></div>)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// === ОЧИЩЕННАЯ МОДАЛКА ФОНДА ИЗ DataTab.js (Убрали "Основной") ===
function FundModal({ fund, data, uOrg, t, tPct, onClose, onArchive, onShowHistory, apiSync }) {
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState(fund.name);
  const [percent, setPercent] = useState(fund.percent.toString());
  const [showCatPicker, setShowCatPicker] = useState(false);

  const myCats = (data.expenseCategories || []).filter(c => !c.archived && c.fundId === fund.id);
  const availCats = (data.expenseCategories || []).filter(c => !c.archived && c.fundId !== fund.id);

  const save = async () => {
    const pct = parseFloat(percent);
    if (!name.trim() || isNaN(pct) || pct < 0) return;
    const other = data.funds.filter(x => !x.archived && x.id !== fund.id).reduce((s, x) => s + x.percent, 0);
    if (other + pct > 100) return alert(S().data.fundPercentExceeds);
    // F3 (2026-06-08): single-source — без оптимистики, ждём backend → событие refetch.
    if (apiSync && apiSync.syncUpdateFund) await apiSync.syncUpdateFund(fund.id, { name: name.trim(), percent: pct });
    try { window.dispatchEvent(new CustomEvent('inmonex-refetch-funds')); } catch (e) {}
    setIsEdit(false);
  };

  // Stage 20b B.3: REST PUT /categories.
  const toggleCat = (cId) => {
    const cur = (data.expenseCategories || []).find(c => c.id === cId);
    const newFundId = cur && cur.fundId === fund.id ? '' : fund.id;
    uOrg(p => ({ ...p, expenseCategories: p.expenseCategories.map(c => c.id === cId ? { ...c, fundId: newFundId } : c) }));
    if (apiSync && apiSync.syncUpdateCategory) apiSync.syncUpdateCategory(cId, { fundId: newFundId });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, borderRadius: 20, width: 500, maxHeight: '90vh', padding: 24, display: 'flex', flexDirection: 'column', border: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setIsEdit(!isEdit)} style={{ background: t.bgInput, color: t.textSec, padding: 8, borderRadius: 8, border: `1px solid ${t.border}`, cursor: 'pointer' }}><Edit3 size={18} /></button>
            <button onClick={onArchive} style={{ background: `${t.accentOrange}15`, color: t.accentOrange, padding: 8, borderRadius: 8, border: 'none', cursor: 'pointer' }}><Archive size={18} /></button>
          </div>
          <button onClick={onClose} style={{ color: t.textMuted, background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <div style={{ overflowY: 'auto' }}>
          {isEdit ? (
            <div style={{ display: 'grid', gap: 12, marginBottom: 20, background: t.bgInput, padding: 14, borderRadius: 12 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={name} onChange={e => setName(e.target.value)} style={{ flex: 2, fontSize: 16, padding: 8, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text }} autoFocus />
                <input type="number" value={percent} onChange={e => setPercent(e.target.value)} style={{ width: 80, fontSize: 16, padding: 8, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text }} />
              </div>
              <Btn variant="success" style={{ justifyContent: 'center', marginTop: 10 }} onClick={save}><Check size={18} /> {S().common.save}</Btn>
            </div>
          ) : (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontSize: 24, fontWeight: 800 }}>{fund.name} <span style={{ fontSize: 16, color: t.textMuted }}>{fund.percent}%</span></h2>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <div style={{ padding: '4px 12px', background: `${t.accentPurple}15`, color: t.accentPurple, borderRadius: 20, fontSize: 13, fontWeight: 700 }}>{S().data.inFundPrefix(fmt(data.fundBalances[fund.id] || 0))}</div>
                <button onClick={onShowHistory} style={{ color: t.accentCyan, background: 'transparent', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, border: 'none', cursor: 'pointer' }}><History size={14} /> {S().data.historyBtn}</button>
              </div>
            </div>
          )}

          <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Tags size={16} color={t.accentPurple} /> {S().data.expenseCategories}</span>
              <button onClick={() => setShowCatPicker(!showCatPicker)} style={{ color: t.accent, background: 'transparent', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer' }}><Plus size={14} /> {S().common.add}</button>
            </div>
            {showCatPicker && <div style={{ background: t.bgInput, padding: 10, borderRadius: 8, marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
              {availCats.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().data.noFreeExpenseCategories}</div> : availCats.map(c => <div key={c.id} onClick={() => toggleCat(c.id)} style={{ padding: '8px', cursor: 'pointer', borderBottom: `1px solid ${t.border}`, fontSize: 13 }}>{c.name}</div>)}
            </div>}
            <div style={{ display: 'grid', gap: 6 }}>
              {myCats.length === 0 ? <div style={{ fontSize: 12, color: t.textMuted }}>{S().data.noLinkedExpenseCategories}</div> : myCats.map(c => <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: t.bgInput, borderRadius: 10 }}><span>{c.name}</span><button onClick={() => toggleCat(c.id)} style={{ color: t.accentRed, background: 'transparent', fontSize: 11, border: 'none', cursor: 'pointer' }}>{S().data.removeLink}</button></div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}// === НОВОЕ: МОДАЛКА ПОПОЛНЕНИЯ / ИЗЪЯТИЯ ДЛЯ СКРЫТЫХ СЧЕТОВ ===
function QuickHiddenAdjustModal({ data, uOrg, user, accId, type, onClose, apiSync }) {
  const t = useTheme();
  const [amt, setAmt] = useState('');
  const [cmt, setCmt] = useState('');
  const acc = (data.accounts || []).find(a => a.id === accId);
  const isInc = type === 'income';

  const submit = () => {
    const sum = parseFloat(amt);
    if (!sum || sum <= 0) { alert(S().data.enterAmount); return; }

    const currentBal = data.accountBalances[accId] || 0;
    if (!isInc && sum > currentBal) { alert(S().data.insufficientStash(fmt(currentBal))); return; }

    // Stage 4.0 finishing (2026-05-12): outer var для tx — для apiSync после uOrg.
    let builtTx = null;

    uOrg(p => {
      // 1. Корректируем баланс скрытого счета
      const nA = { ...p.accountBalances, [accId]: (p.accountBalances[accId] || 0) + (isInc ? sum : -sum) };

      // 2. Создаем "теневую" транзакцию (без категорий и фондов)
      const tx = {
        id: uid(),
        type: isInc ? 'income' : 'expense',
        accountId: accId,
        amount: sum,
        comment: cmt || (isInc ? S().data.topUpComment : S().data.withdrawComment),
        isHidden: true, // ВАЖНО: Этот флаг скажет системе полностью игнорировать транзакцию в отчетах!
        userId: user?.id || 'sys',
        userName: user?.name || S().common.unknown,
        date: new Date().toISOString()
      };
      builtTx = tx;

      return { ...p, accountBalances: nA, transactions: [tx, ...(p.transactions || [])] };
    });

    // Stage 4.0 finishing: explicit POST /operations
    if (apiSync && builtTx) apiSync.syncCreateOperation(builtTx);

    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1.5px solid ${isInc ? t.accentGreen : t.accentRed}`, borderRadius: 16, padding: 24, width: 400, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{isInc ? S().data.topUpStash : S().data.withdrawStash}</div>
          <button onClick={onClose} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div><div style={{ fontSize: 13, color: t.textMuted }}>{S().data.hiddenAccountName}</div><div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{acc?.name}</div></div>
          <div style={{ textAlign: 'right' }}><div style={{ fontSize: 13, color: t.textMuted }}>{S().data.stashNow}</div><div className="mono" style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{fmt(data.accountBalances[accId] || 0)}</div></div>
        </div>
        <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
          <div><label style={{ fontSize: 12, color: t.textMuted, marginBottom: 4, display: 'block' }}>{isInc ? S().data.topUpAmount : S().data.withdrawAmount}</label><NumInput value={amt} onChange={setAmt} /></div>
          <div><label style={{ fontSize: 12, color: t.textMuted, marginBottom: 4, display: 'block' }}>{S().data.commentForSelf}</label><input value={cmt} onChange={e => setCmt(e.target.value)} placeholder={isInc ? S().data.topUpPlaceholder : S().data.withdrawPlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} /></div>
        </div>
        <Btn variant={isInc ? "success" : "danger"} onClick={submit} style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15 }}>
          {isInc ? <Plus size={16} /> : <Minus size={16} />} {isInc ? S().data.topUpBtn : S().data.withdrawBtn} {amt ? fmt(parseFloat(amt)) : ''}
        </Btn>
      </div>
    </div>
  );
}